import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchUsers,
  createUser,
  updateUserData,
  deleteUserById,
  fetchUserById,
} from "../../store/features/user/userSlice";
import { Button, Table, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { BsPencilSquare, BsTrash } from "react-icons/bs";
import {
  FaCrown,
  FaUserTie,
  FaUserShield,
  FaUsers,
  FaUser,
  FaChevronUp,
  FaChevronDown,
} from "react-icons/fa";
import { AiOutlineAudit } from "react-icons/ai";
import { FaFileDownload } from "react-icons/fa";

import UserModal from "./components/modals/UserModal";
import FilterData from "./components/FilterData";
import Roles from "./components/Roles";
import PageTitle from "../../components/pageTitle";

const countRolesRecursively = (users) => {
  const roleCounts = {};

  const traverse = (userList) => {
    for (const user of userList) {
      roleCounts[user.role] = (roleCounts[user.role] || 0) + 1;

      if (user.children) traverse(user.children);
      if (user.createdUsers) traverse(user.createdUsers);
    }
  };

  traverse(users);
  return roleCounts;
};

function UserManagement() {
  const dispatch = useDispatch();
  const {
    users = [],
    loading,
    usersCreatedBy = [],
  } = useSelector((state) => state.users);
  const { projects } = useSelector((state) => state.projects);
  const { role, _id } = useSelector((state) => state.auth);

  const [editingUser, setEditingUser] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [currentRole, setCurrentRole] = useState(null);
  const [currentProject, setCurrentProject] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedUsers, setExpandedUsers] = useState(new Set());

  const usersDataArray = useMemo(
    () => (role === "superadmin" ? users : usersCreatedBy),
    [role, users, usersCreatedBy]
  );

  useEffect(() => {
    dispatch(role === "superadmin" ? fetchUsers() : fetchUserById(_id));
  }, [dispatch, role, _id]);

  const isEditing = Boolean(editingUser?._id); // <-- Add this

  const validationSchema = useMemo(
    () =>
      Yup.object({
        name: Yup.string().required("Name is required"),
        email: Yup.string()
          .email("Invalid email")
          .required("Email is required"),
        phone: Yup.string().required("Phone is required"),
        country: Yup.string().required("Country is required"),
        state: Yup.string().required("State is required"),
        city: Yup.string().required("City is required"),
        password: Yup.string().when([], {
          is: () => !isEditing,
          then: (schema) =>
            schema
              .min(6, "Password must be at least 6 characters")
              .required("Password is required"),
          otherwise: (schema) => schema.notRequired(),
        }),
        confirmPassword: Yup.string().when("password", {
          is: (val) => !!val,
          then: (schema) =>
            schema
              .oneOf([Yup.ref("password"), null], "Passwords must match")
              .required("Confirm Password is required"),
          otherwise: (schema) => schema.notRequired(),
        }),
        org: Yup.string().required("Organization is required"),
        role: Yup.string().required("Role is required"),
      }),
    [isEditing]
  );

  const formik = useFormik({
    initialValues: {
      name: editingUser?.name || "",
      phone: editingUser?.phone || "",
      email: editingUser?.email || "",
      role: editingUser?.role || "",
      password: editingUser?.password || "",
      country: editingUser?.country || "",
      city: editingUser?.city || "",
      state: editingUser?.state || "",
      org: editingUser?.org || "",
      avatar: editingUser?.avatar || "",
      uploadCv: editingUser?.uploadCv || "",
      assistantSuperAdminId: editingUser?.assistantSuperAdminId || "",
      managerId: editingUser?.managerId || "",
      staffId: editingUser?.staffId || "",
    },
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        const action = editingUser?._id
          ? updateUserData({ ...values, _id: editingUser._id })
          : createUser(values);
        await dispatch(action).unwrap();
        toast.success(editingUser?._id ? "User updated!" : "User created!");
        dispatch(role === "superadmin" ? fetchUsers() : fetchUserById(_id));
        resetForm();
        setShowForm(false);
        setEditingUser({});
      } catch (err) {
        toast.error(err || "Error saving user");
      }
    },
  });

  const handleEdit = (user) => {
    setEditingUser(user);
    setShowForm(true);
  };

  const handleDelete = useCallback(
    async (id) => {
      try {
        await dispatch(deleteUserById(id)).unwrap();
        dispatch(fetchUsers());
        toast.info("User deleted");
      } catch (err) {
        toast.error("Error deleting user");
      }
    },
    [dispatch]
  );

  const toggleUserExpand = useCallback((userId) => {
    setExpandedUsers((prev) => {
      const newSet = new Set(prev);
      newSet.has(userId) ? newSet.delete(userId) : newSet.add(userId);
      return newSet;
    });
  }, []);

  const roleCounts = useMemo(
    () => countRolesRecursively(usersDataArray),
    [usersDataArray]
  );

  useEffect(() => {
    if (editingUser.password) {
      formik.setFieldValue("confirmPassword", editingUser.password);
    } else {
      formik.setFieldValue("confirmPassword", "");
    }
  }, [editingUser.password]);

  const userRoles = useMemo(
    () => [
      {
        role: "superadmin",
        icon: <FaCrown color="#d22e2d" />,
        color: "#fde2e2",
        count: roleCounts["superadmin"] || 0,
      },
      {
        role: "assistant superadmin",
        icon: <FaUserShield color="#c607c6" />,
        color: "#fdddfd",
        count: roleCounts["assistant superadmin"] || 0,
      },
      {
        role: "manager",
        icon: <FaUserTie color="#3a63a8" />,
        color: "#d6e4fc",
        count: roleCounts["manager"] || 0,
      },
      {
        role: "staff",
        icon: <FaUsers color="#069d57" />,
        color: "#d3f9e7",
        count: roleCounts["staff"] || 0,
      },
      {
        role: "auditor",
        icon: <AiOutlineAudit color="#ee6b01" />,
        color: "#fcf3d8",
        count: roleCounts["auditor"] || 0,
      },
    ],
    [roleCounts]
  );

  const filterUsersRecursively = useCallback(
    (users) => {
      return users
        .map((user) => {
          const nameMatch = user.name
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase());
          const roleMatch = currentRole ? user.role === currentRole : true;
          const projectMatch = currentProject
            ? user.projects?.some((p) => p.projectName === currentProject)
            : true;

          const matches = nameMatch && roleMatch && projectMatch;

          // Recursively filter children and createdUsers
          const filteredChildren = user.children
            ? filterUsersRecursively(user.children)
            : [];
          const filteredCreatedUsers = user.createdUsers
            ? filterUsersRecursively(user.createdUsers)
            : [];

          const hasMatchingChildren = filteredChildren.length > 0;
          const hasMatchingCreatedUsers = filteredCreatedUsers.length > 0;

          if (matches || hasMatchingChildren || hasMatchingCreatedUsers) {
            return {
              ...user,
              children: filteredChildren,
              createdUsers: filteredCreatedUsers,
            };
          }

          return null;
        })
        .filter(Boolean);
    },
    [searchTerm, currentRole, currentProject]
  );

  const filteredUsers = useMemo(() => {
    return filterUsersRecursively(usersDataArray);
  }, [usersDataArray, filterUsersRecursively]);

  const renderUserRow = (user, level = 0) => {
    const allChildren = [
      ...(user.children || []),
      ...(user.createdUsers || []),
    ];
    const isExpanded = expandedUsers.has(user._id);
    const indentStyle = { paddingLeft: `${level * 20}px` };

    return (
      <React.Fragment key={user._id}>
        <tr>
          <td>
            <div className="d-flex align-items-center" style={indentStyle}>
              {allChildren.length > 0 ? (
                <Button
                  variant="link"
                  size="sm"
                  onClick={() => toggleUserExpand(user._id)}
                  style={{
                    textDecoration: "none",
                    marginRight: "8px",
                    padding: 0,
                  }}
                >
                  {isExpanded ? (
                    <FaChevronDown color="#4b5563" />
                  ) : (
                    <FaChevronUp color="#4b5563" />
                  )}
                </Button>
              ) : (
                <span style={{ width: "24px" }} />
              )}
              {user.avatar ? (
                <img
                  src={user.avatar}
                  height={32}
                  width={32}
                  className="icon-avatar"
                  alt="avatar"
                />
              ) : (
                <span className="icon-avatar">
                  <FaUser />
                </span>
              )}
              <div>
                <span className="name">{user.name}</span>
                <span className="email"> {user.email}</span>
              </div>
            </div>
          </td>
          <td>{user.role}</td>
          {(role === "superadmin" || role === "assistant superadmin") && (
            <td>
              {user.projects?.length
                ? user.projects.map((p) => p.projectName).join(", ")
                : "-"}
            </td>
          )}
          {role !== "staff" && (
            <td>
              {user.workPackages?.length
                ? user.workPackages.map((wp) => wp.title).join(", ")
                : "-"}
            </td>
          )}
          <td>
            {user.tasks?.length
              ? user.tasks.map((t) => t.title).join(", ")
              : "-"}
          </td>
          <td>
            {user.uploadCv && (
              <a
                href={user.uploadCv}
                download
                target="_blank"
                rel="noopener noreferrer"
              >
                <FaFileDownload color="#4b5563" />
              </a>
            )}
          </td>
          <td className="actions">
            <Button size="sm" onClick={() => handleEdit(user)} title="edit">
              <BsPencilSquare />
            </Button>{" "}
            {user._id !== _id && (
              <Button
                size="sm"
                variant="danger"
                onClick={() => handleDelete(user._id)}
                title="delete"
              >
                <BsTrash />
              </Button>
            )}
          </td>
        </tr>
        {isExpanded &&
          allChildren.map((child) => renderUserRow(child, level + 1))}
      </React.Fragment>
    );
  };

  return (
    <>
      <PageTitle
        setShowForm={setShowForm}
        formik={formik}
        setEditing={setEditingUser}
        pageTitle="User Management"
        pageSubtitle="Manage users, roles, and permissions across the platform"
        actionName={role === "staff" ? "" : "Add User"}
      />

      <Roles roles={userRoles} currentRole={role} />

      {loading ? (
        <Spinner animation="border" />
      ) : (
        <div className="table-wrapper">
          <FilterData
            currentRole={currentRole}
            setCurrentRole={setCurrentRole}
            userRoles={
              role === "superadmin" || role === "auditor"
                ? userRoles
                : role === "assistant superadmin"
                ? userRoles.filter((r) => r.role === "manager" || r.role === "staff")
                : role === "manager"
                ? userRoles.filter((r) => r.role === "staff")
                : []
            }
            currentProject={currentProject}
            setCurrentProject={setCurrentProject}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            projectList={projects}
          />

          <Table responsive>
            <thead>
              <tr>
                <th>User</th>
                <th>Role</th>
                {(role === "superadmin" || role === "assistant superadmin") && (
                  <th>Assigned Projects</th>
                )}
                {role !== "staff" && <th>Assigned Workpackages</th>}
                <th>Assigned Tasks</th>
                <th>CV</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length ? (
                filteredUsers.map((user, index) => renderUserRow(user, index))
              ) : (
                <tr>
                  <td colSpan={7} className="text-center py-5">
                    <div className="text-muted">
                      <FaUser size={50} className="mb-3" />
                      <h5>No users found</h5>
                      <p>Try adjusting your filters or search criteria.</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>

          {showForm && (
            <UserModal
              formik={formik}
              showForm={showForm}
              setShowForm={setShowForm}
              editingUser={editingUser}
              setEditingUser={setEditingUser}
            />
          )}
        </div>
      )}
    </>
  );
}

export default UserManagement;
